import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { titleize } from 'underscore.string';
import _ from 'lodash';
import { OverlayTrigger } from 'react-bootstrap';
import ToolTip from 'components/v2/Tooltip';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import { sluggify } from 'utils/localization/utils';
import { getInvoicesForShipment } from 'utils/AccountingWebAPIUtils';
import { withTranslation, Translation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { getShipmentTimezone } from 'helpers/shipments';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';
import CargomaticAlert from 'components/ui/Alert';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { Table, TableHeader, TableHead, TableRow, TableCell, TableBody, TableFooter, } from '@cargomatic/ui';
import { checkProcessedAccessorial, checkIfAccessorialsNeedsReview, checkIfEquipmentIsMissing, checkIfEquipmentIsMissingForAccessorial, checkIfAccessorialAutoDeclined, } from 'helpers/accessorials';
const TOOLTIPS = {
    SHIPPER_CHANGED: (React.createElement(ToolTip, { id: "shipper_changed_accessorial_request", style: { zIndex: 99999 } },
        React.createElement(Translation, null, (t) => t('SHIPPER_CHANGED_NOTE')))),
};
class AccessorialRequests extends Component {
    constructor(props) {
        super(props);
        this.getInvoices = async () => {
            const shipmentId = this.props.shipment._id;
            const { entity: { invoices }, } = await getInvoicesForShipment(shipmentId);
            this.setState((prev) => {
                return Object.assign(Object.assign({}, prev), { invoices });
            });
        };
        this.getElapsedTime = (time) => {
            const now = moment();
            const timeToDiff = moment(time);
            const days = now.diff(timeToDiff, 'days');
            const hours = now.diff(timeToDiff, 'hours');
            const minutes = now.diff(timeToDiff, 'minutes');
            const seconds = now.diff(timeToDiff, 'seconds');
            if (days > 0) {
                return `${days}d`;
            }
            if (hours > 0) {
                return `${hours}h`;
            }
            if (minutes > 0) {
                return `${minutes}m`;
            }
            return `${seconds}s`;
        };
        this.getStatus = (accessorial) => {
            const { carrier_status, shipper_status } = accessorial;
            const statusesDiffer = carrier_status && shipper_status && carrier_status !== shipper_status;
            if (statusesDiffer) {
                return {
                    carrier_status,
                    shipper_status,
                };
            }
            return { status: shipper_status };
        };
        this.calculateCost = (rate, qty = 1) => {
            if (!rate) {
                return 0;
            }
            return parseFloat(rate) * parseFloat(qty);
        };
        this.accessorialIsSelected = (accessorial) => this.state.selectedAccessorials.includes(accessorial._id);
        this.allAccessorialsSelected = () => {
            const { accessorials, selectedAccessorials } = this.state;
            return (accessorials.length === selectedAccessorials.length &&
                selectedAccessorials.length);
        };
        this.selectAll = (e) => {
            const { accessorials } = this.state;
            const selectedAccessorials = e.target.checked ? accessorials : [];
            const unprocessedAccessorials = selectedAccessorials.filter((accessorial) => !checkProcessedAccessorial(accessorial) &&
                !checkIfAccessorialAutoDeclined(accessorial));
            const selectedIds = unprocessedAccessorials.map((acc) => acc._id);
            this.setState({ selectedAccessorials: selectedIds }, () => this.props.onAccessorialSelect(unprocessedAccessorials));
        };
        this.selectAccessorial = (e, id) => {
            const checked = e.target.checked;
            this.setState((currentState) => {
                const { selectedAccessorials: currentAccessorials } = currentState;
                const selectedAccessorials = checked
                    ? currentAccessorials.concat(id)
                    : currentAccessorials.filter((acc) => acc !== id);
                const fullSelectedAccessorials = this.state.accessorials.filter((acc) => selectedAccessorials.includes(acc._id));
                this.props.onAccessorialSelect(fullSelectedAccessorials);
                return {
                    selectedAccessorials,
                };
            });
        };
        this.renderMissingDocumentsLabel = () => {
            const { accessorials } = this.state;
            // Don't show the missing document label if ALL accessorials are declined
            const allAccessorialsDeclined = accessorials.every((accessorial) => {
                const { status } = this.getStatus(accessorial);
                return status === 'declined';
            });
            if (allAccessorialsDeclined)
                return;
            if (accessorials && accessorials.length) {
                const { t } = this.props;
                const requestsAreMissingDocuments = accessorials.some((acc) => {
                    var _a;
                    const isAccessorialNotDeclined = ((_a = this.getStatus(acc)) === null || _a === void 0 ? void 0 : _a.status) !== 'declined';
                    return isAccessorialNotDeclined && acc.missing_docs;
                });
                if (requestsAreMissingDocuments) {
                    return (React.createElement(CargomaticAlert, { dataTestId: "CargomaticAlert-5c604c0d", className: "my-4", type: "error" }, t('ACCESSORIALS_WITH_MISSING_DOCUMENTS')));
                }
            }
        };
        this.renderHeader = () => {
            const { t } = this.props;
            return (React.createElement(TableRow, { "data-testid": "tr-53e40358" },
                React.createElement(TableHead, { "data-testid": "th-ed2f9396" }, this.renderSelectAll()),
                React.createElement(TableHead, { "data-testid": "th-bb6ee6b9", className: "" }, t('ADJUSTMENT')),
                React.createElement(TableHead, { "data-testid": "th-1b53eb03", className: "text-right" }, t('SHIPPER_COST')),
                React.createElement(TableHead, { "data-testid": "th-1b22eb22" }, t('SHIPPER_SOURCE')),
                React.createElement(TableHead, { "data-testid": "th-0d2e9168", className: "text-right" }, t('CARRIER_OR_VENDOR_COST')),
                React.createElement(TableHead, { "data-testid": "th-2253eb2" }, t('CARRIER_SOURCE')),
                React.createElement(TableHead, { "data-testid": "th-b8729c3f" }, titleize(t('NAME', { count: 1 }))),
                React.createElement(TableHead, { "data-testid": "th-1b9b0b78" }, t('STATUS')),
                React.createElement(TableHead, { "data-testid": "th-b8729c3f" }, t('CREATED_BY')),
                React.createElement(TableHead, { "data-testid": "th-f64267fb" },
                    t('MISSING_DOCUMENTS'),
                    "?"),
                React.createElement(TableHead, { "data-testid": "th-3e42809c" }, t('ACTION_REQUIRED')),
                React.createElement(TableHead, { "data-testid": "th-3028f0d1" }, t('UPDATED'))));
        };
        this.renderFooter = () => {
            var _a, _b;
            const { t } = this.props;
            let shipperTotal = 0;
            let carrierTotal = 0;
            this.state.accessorials.forEach((accessorial) => {
                const { carrier_rate, shipper_rate, carrier_unit_qty, shipper_unit_qty } = accessorial;
                shipperTotal += this.calculateCost(shipper_rate || 0, shipper_unit_qty);
                carrierTotal += this.calculateCost(carrier_rate || 0, carrier_unit_qty);
            });
            return (React.createElement(TableRow, { "data-testid": "tr-0c507a61" },
                React.createElement(TableCell, { "data-testid": "td-bec09f44" }),
                React.createElement(TableCell, { "data-testid": "td-73ce3420", className: "uppercase" }, t('TOTAL')),
                React.createElement(TableCell, { "data-testid": "td-95882bf1", className: "uppercase font-bold text-right" }, formatMoney(shipperTotal, (_a = this.state.accessorials[0]) === null || _a === void 0 ? void 0 : _a.currency)),
                React.createElement(TableCell, { "data-testid": "td-73220990" }, "\u00A0"),
                React.createElement(TableCell, { "data-testid": "td-05c0b30d", className: "uppercase font-bold text-right" }, formatMoney(carrierTotal, (_b = this.state.accessorials[0]) === null || _b === void 0 ? void 0 : _b.currency)),
                React.createElement(TableCell, { colSpan: 7 }, "\u00A0")));
        };
        this.renderBody = () => {
            return this.state.accessorials.map((accessorial) => this.renderRow(accessorial));
        };
        this.renderStatusColumns = (accessorial) => {
            const { t } = this.props;
            // Something to be aware of: getStatus returns 'status' if both statuses are the same,
            // otherwise it returns the individual statuses.
            let { shipper_status, carrier_status, status } = this.getStatus(accessorial);
            if (!status) {
                carrier_status =
                    (carrier_status || '').indexOf('pending') > -1
                        ? 'pending'
                        : carrier_status;
                shipper_status =
                    (shipper_status || '').indexOf('pending') > -1
                        ? 'pending'
                        : shipper_status;
                return (React.createElement("div", { "data-testid": "div-3f1228a1", className: "flex flex-col space-y-1" },
                    React.createElement("span", { "data-testid": "span-78cc89b3", className: `px-2 py-1 rounded text-white text-sm font-bold ${shipper_status}` }, titleize(t(sluggify(shipper_status)))),
                    React.createElement("span", { "data-testid": "span-5e135712", className: `px-2 py-1 rounded text-white text-sm font-bold ${carrier_status}` }, titleize(t(sluggify(carrier_status))))));
            }
            status = (status || '').indexOf('pending') > -1 ? 'pending' : status;
            return [
                React.createElement("span", { "data-testid": "span-13fc3506", className: `px-2 py-1 rounded text-white text-sm font-bold ${status}` }, titleize(t(sluggify(status)))),
            ];
        };
        this.renderMissingDocumentsColumn = ({ shipperAutoDeclineDate }) => {
            if (!(shipperAutoDeclineDate && this.props.shipment)) {
                return null;
            }
            const timezone = getShipmentTimezone(this.props.shipment);
            return (React.createElement("div", { "data-testid": "div-bf4d3ea9", className: "flex text-red-700 justify-between" },
                React.createElement(ExclamationIcon, { className: "w-6 h-6 m-auto" }),
                React.createElement("p", { "data-testid": "p-2ba8bb00", className: "pl-1" }, `Due on ${moment
                    .tz(shipperAutoDeclineDate, timezone)
                    .format('L')}`)));
        };
        this.renderRow = (accessorial) => {
            const { t } = this.props;
            const { shipper_status, status } = this.getStatus(accessorial);
            const { _id } = accessorial;
            let { action_required: actionRequired } = accessorial;
            const isMissingEquipment = checkIfEquipmentIsMissingForAccessorial(accessorial);
            const unappliedTooltipId = `unapplied-${_id}`;
            if (['declined', 'approved', 'accepted'].includes(actionRequired)) {
                actionRequired = '';
            }
            const shouldRenderMissingDocumentsDueDate = featureEnabled(CONFIGURED_FEATURES.SSR) && status !== 'declined';
            return (React.createElement(TableRow, { "data-testid": "tr-b6ab6c53", className: "" },
                React.createElement(TableCell, { "data-testid": "td-09bcabc0" }, this.renderSelect(accessorial)),
                React.createElement(TableCell, { "data-testid": "td-33651e18", className: "" },
                    React.createElement("div", { className: "flex gap-2" },
                        React.createElement("a", { "data-testid": "a-90d45db8", onClick: () => this.props.handleRequestSelect(accessorial) }, t(sluggify(accessorial.message))),
                        isMissingEquipment && (React.createElement(ToolTip, { dataTestId: "ToolTip-Missing-Equipment-aae84eb1", id: unappliedTooltipId, body: this.renderWarningIcon }, t('NOT_APPLIED_TO_AN_EQUIPMENT'))))),
                React.createElement(TableCell, { "data-testid": "td-cb5b9c03", className: "text-right" }, (accessorial.shipper_rate < 0 || accessorial.shipper_rate > 0) &&
                    `${formatMoney(this.calculateCost(accessorial.shipper_rate, accessorial.shipper_unit_qty), accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency)}`),
                React.createElement(TableCell, { "data-testid": "td-ab220990" }, t(sluggify((accessorial === null || accessorial === void 0 ? void 0 : accessorial.receivable_rate_source) || ''))),
                React.createElement(TableCell, { "data-testid": "td-3540c6ee", className: "text-right" }, (accessorial.carrier_rate < 0 || accessorial.carrier_rate > 0) &&
                    `${formatMoney(this.calculateCost(accessorial.carrier_rate, accessorial.carrier_unit_qty), accessorial === null || accessorial === void 0 ? void 0 : accessorial.currency)}`),
                React.createElement(TableCell, { "data-testid": "td-cb09c22" }, t(sluggify((accessorial === null || accessorial === void 0 ? void 0 : accessorial.payable_rate_source) || ''))),
                React.createElement(TableCell, { "data-testid": "td-b6043b90", className: "line-clamp-3" }, accessorial.payee_type === 'carrier'
                    ? unescapeSpecialChars(accessorial.carrier_name)
                    : unescapeSpecialChars(accessorial.vendor_name)),
                React.createElement(TableCell, { "data-testid": "td-923358b7" }, this.renderStatusColumns(accessorial)),
                React.createElement(TableCell, { "data-testid": "td-911358b7" }, accessorial === null || accessorial === void 0 ? void 0 : accessorial.created_by),
                shouldRenderMissingDocumentsDueDate && (React.createElement(TableCell, { "data-testid": "td-ad8d5a18", style: { color: '#ddd' } }, this.renderMissingDocumentsColumn(accessorial))),
                React.createElement(TableCell, { "data-testid": "td-185df130", style: {
                        color: (status || shipper_status) === 'pending-admin'
                            ? '#00a9db'
                            : '#ddd',
                    } },
                    React.createElement("a", { "data-testid": "a-882ae2ca", onClick: () => this.props.handleRequestSelect(accessorial) }, t(sluggify(actionRequired)))),
                React.createElement(TableCell, { "data-testid": "td-d1a9183a", style: { color: '#00a9db' } },
                    React.createElement("a", { "data-testid": "a-6a39eb6e", onClick: () => this.props.handleRequestSelect(accessorial) }, this.getElapsedTime(accessorial.modified_at || accessorial.created_at)))));
        };
        this.state = {
            accessorials: props.accessorials || [],
            accessorialRequestIds: props.accessorialRequestIds || [],
            selectedAccessorials: [],
            invoices: [],
            autoDeclineDates: {},
        };
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevState.accessorials !== this.state.accessorials) {
            this.getInvoices();
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            selectedAccessorials: nextProps.selectedAccessorials,
            accessorials: nextProps.accessorials,
            accessorialRequestIds: nextProps.accessorialRequestIds,
        });
    }
    componentWillUnmount() { }
    renderWarningIcon() {
        return React.createElement(ExclamationIcon, { className: "text-red-700 w-5 h-5" });
    }
    renderSelect(accessorial) {
        const accessorialDeclinedBothSide = checkIfAccessorialAutoDeclined(accessorial);
        const disabled = checkProcessedAccessorial(accessorial) || accessorialDeclinedBothSide;
        if (accessorialDeclinedBothSide) {
            return (React.createElement(OverlayTrigger, { placement: "top", overlay: TOOLTIPS.SHIPPER_CHANGED },
                React.createElement("input", { "data-testid": "input-4debbc29", className: "checkbox-center", type: "checkbox", checked: this.accessorialIsSelected(accessorial), onChange: (e) => this.selectAccessorial(e, accessorial._id), disabled: disabled })));
        }
        return (React.createElement("input", { "data-testid": "input-4debbc29", className: "checkbox-center", type: "checkbox", checked: this.accessorialIsSelected(accessorial), onChange: (e) => this.selectAccessorial(e, accessorial._id), disabled: disabled }));
    }
    renderSelectAll() {
        return (React.createElement("input", { "data-testid": "input-680e7fe6", className: "checkbox-center", type: "checkbox", checked: this.allAccessorialsSelected(), onChange: this.selectAll }));
    }
    render() {
        const { dataTestId, t } = this.props;
        const { selectedAccessorials = [], accessorials = [] } = this.state;
        const selectAccessorialsForAutoAppliedNeedReview = accessorials.filter((accessorial) => selectedAccessorials.includes(accessorial._id));
        return (React.createElement("div", { "data-testid": dataTestId, className: "overflow-x-auto" },
            featureEnabled(CONFIGURED_FEATURES.SSR) &&
                this.renderMissingDocumentsLabel(),
            checkIfAccessorialsNeedsReview(selectAccessorialsForAutoAppliedNeedReview) && (React.createElement(CargomaticAlert, { dataTestId: "Alert-Auto-Apply-Bulk-Accessorial", className: "my-4", type: "warning" }, t('AUTO_APPLY_BULK_ACCESSORIAL_WARNING_MESSAGE'))),
            checkIfEquipmentIsMissing(selectAccessorialsForAutoAppliedNeedReview) && (React.createElement(CargomaticAlert, { dataTestId: "Alert-Equipment-Bulk-Accessorial", className: "my-4", type: "error" }, t('NOT_APPLIED_TO_AN_EQUIPMENT'))),
            React.createElement(Table, { "data-testid": "table-87053597" },
                React.createElement(TableHeader, { "data-testid": "thead-221b5d84" }, this.renderHeader()),
                React.createElement(TableBody, { "data-testid": "tbody-923a97c6" }, this.renderBody()),
                React.createElement(TableFooter, { "data-testid": "tfoot-cad95bc9" }, this.renderFooter()))));
    }
}
AccessorialRequests.propTypes = {
    shipment: PropTypes.object.isRequired,
    handleRequestSelect: PropTypes.func,
    onAccessorialSelect: PropTypes.func.isRequired,
};
export default withTranslation()(AccessorialRequests);
